/**
 * Copyright © 2025 Delicious AI, LLC
 *
 * @author Bryan Sheranian <bryan.sheranian@deliciousai.com>
 */

import {
  Document,
  Image,
  Page,
  StyleSheet,
  Text,
  View,
} from '@react-pdf/renderer'
import React from 'react'

import { ProductFragment } from '@/graphql'

const styles = StyleSheet.create({
  page: {
    padding: 20,
  },
  container: {
    display: 'flex',
    flex: 1,
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
  },
  displayTitle: {
    fontSize: 16,
    marginBottom: 10,
  },
  storeTitle: {
    fontSize: 10,
    color: '#666',
  },
  header: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    justifyContent: 'flex-start',
  },
})

interface PlanogramPDFProps {
  t: (key: string) => string
  displays: {
    name: string | null | undefined
    storeName: string | null | undefined
    reversed?: boolean
    products: (Pick<
      ProductFragment,
      'upcA' | 'planogramImageUrl' | 'marketingImageUrl'
    > & { missing?: boolean })[][]
  }[]
}

const A4_HEIGHT = 841
const PAGE_PADDING = 40
const HEADER_HEIGHT = 40
const PLANOGRAM_CONTAINER_OVERHEAD = 32
const PER_SHELF_OVERHEAD = 10 // gap + marginBottom + divider

function calcShelfHeight(numShelves: number): number {
  if (numShelves <= 0) return 100
  const available =
    A4_HEIGHT -
    PAGE_PADDING -
    HEADER_HEIGHT -
    PLANOGRAM_CONTAINER_OVERHEAD -
    PER_SHELF_OVERHEAD * numShelves
  return Math.min(100, Math.max(20, available / numShelves))
}

const PlanogramPDF: React.FC<PlanogramPDFProps> = ({ t, displays }) => {
  return (
    <Document>
      {displays.map((display, index) => {
        const shelfHeight = calcShelfHeight(display.products.length)
        return (
          <Page key={index} size="A4" style={styles.page}>
            {display.storeName && (
              <View style={styles.header}>
                <Text style={styles.storeTitle}>{display.storeName}</Text>
              </View>
            )}
            <View style={styles.container}>
              <Text style={styles.displayTitle}>
                {display.name || t('planogram.pdf.defaultName')}{' '}
                {display.reversed
                  ? t('planogram.pdf.reversedLabel')
                  : t('planogram.pdf.normalLabel')}
              </Text>
              <View style={{ width: '300px' }}>
                <RenderedPlanogramPDF
                  products={display.products}
                  reversed={display.reversed}
                  shelfHeight={shelfHeight}
                />
              </View>
            </View>
          </Page>
        )
      })}
    </Document>
  )
}

export default PlanogramPDF

interface Props {
  products: (Pick<
    ProductFragment,
    'upcA' | 'planogramImageUrl' | 'marketingImageUrl'
  > & { missing?: boolean })[][]
  reversed?: boolean
  shelfHeight?: number
}

const planogramStyles = StyleSheet.create({
  outer: {
    padding: 12,
    width: '100%',
    backgroundColor: '#000',
    borderRadius: 4,
  },
  inner: {
    backgroundColor: '#fff',
    gap: 4,
    padding: 4,
    flexDirection: 'column',
  },
  shelf: {
    width: '100%',
    flexDirection: 'column',
    marginBottom: 4,
  },
  shelfRow: {
    width: '100%',
    flexDirection: 'row',
    gap: 2,
  },
  facing: {
    flex: 1,
    flexDirection: 'row',
  },
  image: {
    width: '100%',
    height: '100%',
    objectFit: 'contain',
  },
  shelfDivider: {
    height: 2,
    width: '100%',
    borderWidth: 1,
    borderColor: '#000',
  },
})

const RenderedPlanogramPDF: React.FC<Props> = ({
  products,
  reversed = false,
  shelfHeight = 100,
}) => {
  return (
    <View style={planogramStyles.outer}>
      <View style={planogramStyles.inner}>
        {products.map((shelf, shelfIndex) => (
          <View key={shelfIndex} style={planogramStyles.shelf}>
            <View style={[planogramStyles.shelfRow, { height: shelfHeight }]}>
              {(reversed ? [...shelf].reverse() : shelf).map(
                (facing, facingIndex) => (
                  <View key={facingIndex} style={planogramStyles.facing}>
                    <Image
                      src={
                        facing.planogramImageUrl ||
                        facing.marketingImageUrl ||
                        '/static/img/products/unknown-product-planogram-facing.png'
                      }
                      style={planogramStyles.image}
                    />
                  </View>
                ),
              )}
            </View>
            <View style={planogramStyles.shelfDivider} />
          </View>
        ))}
      </View>
    </View>
  )
}
